import {
    useGetCampaignAndConnectionAutoReplySettingsQuery,
    useUpdateConnectionAutoReplySettingsMutation,
} from '@copilot/data/graphql/_generated';
import isEmpty from 'lodash/isEmpty';
import { useDispatch } from 'react-redux';
import { toggleAutoReplyInboxMessages } from '@copilot/common/pages/inbox/data/actionCreators';
import notificationManager from '@copilot/common/utils/notificationManager';
import { isAutoReplyPaused } from './util';
import isNil from 'lodash/isNil';
import { AUTO_REPLY_STATUS, AutoReplyParams, AutoReplyResult, AutoReplyStatus } from './types';

/**
 * Hook to use with the auto reply toggle
 * @param isEnabledForCampaign - Whether the auto reply is enabled for the campaign
 * @param isEnabledForConnection - Whether the auto reply is enabled for the connection
 * @param isLastMessageReceived - Whether the last message was received
 * @param autoReplyStatus - Whether auto reply will reply to the last message
 * @returns The auto reply settings
 */
export function useAutoReplyToggle({
    isEnabledForCampaign,
    isEnabledForConnection,
    isLastMessageReceived,
    autoReplyStatus,
}: AutoReplyParams): AutoReplyResult {
    const [updateConnectionAutoReplySettings, { loading }] =
        useUpdateConnectionAutoReplySettingsMutation();

    const dispatch = useDispatch();

    const isPaused = isAutoReplyPaused(
        isEnabledForCampaign && isEnabledForConnection,
        isLastMessageReceived,
        autoReplyStatus
    );

    async function onToggle(connectionId: string, isEnabled: boolean) {
        try {
            await updateConnectionAutoReplySettings({
                variables: {
                    input: {
                        campaignConnectionId: connectionId,
                        isEnabled,
                    },
                },
            });
            dispatch(toggleAutoReplyInboxMessages(connectionId, isEnabled));
        } catch {
            notificationManager.showErrorNotification({
                message: 'Error updating auto reply settings. Please try again.',
                description: 'Unable to update auto reply settings for this connection.',
            });
        }
    }

    function getPopConfirmDetails(): Pick<
        AutoReplyResult,
        'requirePopconfirm' | 'requirePopconfirmReason'
    > {
        if (!isLastMessageReceived) {
            return { requirePopconfirm: false };
        }

        if (isNil(autoReplyStatus)) {
            return { requirePopconfirm: true, requirePopconfirmReason: 'old-message' };
        }

        if (autoReplyStatus === AUTO_REPLY_STATUS.SKIP) {
            // Shown when the agent has decided to not send an auto reply previously
            // Due to the campaign or connection having auto reply disabled
            if (isEnabledForConnection) {
                return {
                    requirePopconfirm: true,
                    requirePopconfirmReason: 'disabled-when-received',
                };
            } else {
                return { requirePopconfirm: true };
            }
        }

        if (autoReplyStatus === AUTO_REPLY_STATUS.DEFER) {
            // Shown when the agent wants to send an auto reply but is waiting for the campaign schedule to be active
            return { requirePopconfirm: true, requirePopconfirmReason: undefined };
        }

        if (autoReplyStatus === AUTO_REPLY_STATUS.STOP) {
            // Shown when the agent has decided to stop sending auto replies due to a stop condition
            return { requirePopconfirm: true, requirePopconfirmReason: 'conversation-signals' };
        }

        return { requirePopconfirm: false };
    }

    return {
        isIndicatorShown: isEnabledForCampaign,
        isEnabled: isEnabledForConnection,
        isPaused,
        onToggle,
        isUpdating: loading,
        ...getPopConfirmDetails(),
    };
}

/**
 * Hook to use with the auto reply toggle. Fetches the auto reply settings for the provided campaignId and connectionId.
 * @param campaignId - The ID of the campaign
 * @param campaignConnectionId - The ID of the campaign connection
 * @param isLastMessageReceived - Whether the last message was received
 * @param autoReplyStatus - The status of the auto reply
 * @returns The auto reply settings and the function to toggle the auto reply
 */
export function useAutoReplyWithData({
    campaignId,
    campaignConnectionId,
    isLastMessageReceived,
    autoReplyStatus,
}: {
    campaignId: string;
    campaignConnectionId: string;
    isLastMessageReceived: boolean;
    autoReplyStatus?: AutoReplyStatus;
}) {
    const { data, loading, refetch } = useGetCampaignAndConnectionAutoReplySettingsQuery({
        variables: {
            campaignId,
            campaignConnectionId,
        },
        skip: isEmpty(campaignId) || isEmpty(campaignConnectionId),
    });

    const campaign = data?.campaign;
    const campaignConnection = data?.campaignConnection;

    const { onToggle: onToggleAutoReply, ...autoReplyDetails } = useAutoReplyToggle({
        isEnabledForCampaign: campaign?.autoReplySettings?.isEnabled ?? false,
        isEnabledForConnection: campaignConnection?.autoReplySettings?.isEnabled ?? false,
        isLastMessageReceived,
        autoReplyStatus,
    });

    async function onToggle(connectionId: string, isEnabled: boolean) {
        await onToggleAutoReply(connectionId, isEnabled);
        refetch();
    }

    return {
        ...autoReplyDetails,
        isLoading: loading,
        onToggle,
    };
}
